import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, isAuthenticatedViaCookie } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(isAuthenticatedViaCookie());

  const checkAuth = useCallback(async () => {
    const hasAuth = isAuthenticatedViaCookie();
    console.log('Checking auth, authenticated:', hasAuth);
    
    if (hasAuth) {
      try {
        const response = await authAPI.getProfile();
        console.log('Auth check successful, user:', response.data);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error.response?.data || error.message);
        if (error.response?.status === 401) {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData } = response.data;
      
      // httpOnly cookie is set automatically by the server
      setIsAuthenticated(true);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  }, []);

  const register = useCallback(async (username, email, password) => {
    try {
      const response = await authAPI.register({ 
        username, 
        email, 
        password, 
        display_name: username
      });
      const { user: userData } = response.data;
      
      // httpOnly cookie is set automatically by the server
      setIsAuthenticated(true);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to clear httpOnly cookies
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
    
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const updateUserProfile = useCallback(async (profileData) => {
    try {
      await authAPI.updateProfile(profileData);
      const response = await authAPI.getProfile();
      setUser(response.data);
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Update failed'
      };
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
